/**
 * Loads one sample summary email for the preview dialog.
 *
 * @link ./NotificationDigestCard for the buttons and the dialog.
 */
import { useCallback, useState } from 'react';
import { notificationsAPI, type NotificationDigestPreviewKind } from '../../../../lib/api';
import { toast } from 'sonner';

const FAILURE = 'Could not load the preview.';

/** A rendered sample email. */
export interface DigestPreview {
  subject: string;
  html: string;
}

export interface UseDigestPreviewResult {
  preview: DigestPreview | null;
  /** The kind being fetched, so its own button can show the spinner. */
  loadingKind: NotificationDigestPreviewKind | null;
  /** Why the last preview did not load, for the card to state. */
  error: string | null;
  open: (kind: NotificationDigestPreviewKind) => void;
  close: () => void;
}

/**
 * @example
 * const { preview, error, open, close } = useDigestPreview();
 * open('daily');
 *
 * @returns the preview on show, the kind in flight, any failure, and open/close.
 */
export function useDigestPreview(): UseDigestPreviewResult {
  const [preview, setPreview] = useState<DigestPreview | null>(null);
  const [loadingKind, setLoadingKind] = useState<NotificationDigestPreviewKind | null>(null);
  const [error, setError] = useState<string | null>(null);

  const open = useCallback((kind: NotificationDigestPreviewKind) => {
    setLoadingKind(kind);
    setError(null);
    notificationsAPI.getDigestPreview(kind)
      .then((res) => setPreview(res.data))
      .catch((err: unknown) => {
        console.error('Digest preview failed:', err);
        setError(FAILURE);
        toast.error(FAILURE);
      })
      .finally(() => setLoadingKind(null));
  }, []);

  const close = useCallback(() => setPreview(null), []);

  return { preview, loadingKind, error, open, close };
}
